/** Court calendar: trials, hearings and court security; evidence in the case and what the court wants done with it. */
import { useMemo, useState } from 'react';
import { COURT_EVENT_LABEL, COURT_ROLE_LABEL, COURT_STATUS_LABEL, DISPOSITION_HINT, DISPOSITION_LABEL, EVIDENCE_STATUS_LABEL, type CourtEventKind, type CourtRole, type CourtStatus, type DispositionDecision } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { Link, useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, EmptyState, PeoplePicker, when } from './common.tsx';

const iso = (d: Date) => d.toLocaleDateString('en-CA');
const addDays = (s: string, n: number) => iso(new Date(Date.parse(`${s}T12:00:00`) + n * 86_400_000));
const dayLabel = (s: string) => new Date(`${s}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
const time = (s: string) => new Date(s).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const STATUS_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'neutral' | 'info'> = { scheduled: 'info', held: 'ok', continued: 'warn', cancelled: 'neutral' };

export function CourtPage() {
  const { navigate } = useRouter();
  const [from, setFrom] = useState(() => iso(new Date()));
  const to = addDays(from, 27);
  const [scheduling, setScheduling] = useState(false);
  const r = useLoad(() => api.get<any>(`/api/court?from=${from}&to=${to}`), [from]);
  const byDay = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const e of r.data?.events ?? []) m.set(iso(new Date(e.starts_at)), [...(m.get(iso(new Date(e.starts_at))) ?? []), e]);
    return [...m.entries()];
  }, [r.data]);
  return (
    <div className="ops stack court">
      <PageHeader
        crumb="Court"
        title="Court calendar"
        sub="Trials, hearings and court security. Everyone scheduled gets a message and sees it on their calendar."
        actions={
          r.data?.canSchedule && (
            <button className="btn primary" onClick={() => setScheduling(true)}>
              Schedule court
            </button>
          )
        }
      />
      {r.data?.evidenceWaiting > 0 && (
        <div className="alert warn">
          <strong>{r.data.evidenceWaiting} item{r.data.evidenceWaiting === 1 ? '' : 's'} in evidence</strong> waiting on the court: what should happen to {r.data.evidenceWaiting === 1 ? 'it' : 'them'}? Open the court date to decide.
        </div>
      )}
      {scheduling && <CourtForm onClose={() => setScheduling(false)} onSaved={(id) => navigate(at(`/court/${id}`))} />}
      <div className="row gap-sm">
        <button className="btn sm" onClick={() => setFrom(addDays(from, -28))} aria-label="Earlier">
          ‹ Earlier
        </button>
        <strong>
          {new Date(`${from}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {new Date(`${to}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </strong>
        <button className="btn sm" onClick={() => setFrom(addDays(from, 28))} aria-label="Later">
          Later ›
        </button>
        <button className="btn sm ghost" onClick={() => setFrom(iso(new Date()))}>
          Today
        </button>
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !byDay.length ? (
        <EmptyState icon="gavel" title="Nothing on the court calendar">
          {r.data.canSchedule ? 'Schedule a trial, hearing or court security.' : 'Court dates show up here when they’re scheduled.'}
        </EmptyState>
      ) : (
        byDay.map(([d, evs]) => (
          <section key={d} className="court-day">
            <h3>{dayLabel(d)}</h3>
            <div className="card flush">
              {evs.map((e) => (
                <button key={e.id} type="button" className={`court-row s-${e.status}`} onClick={() => navigate(at(`/court/${e.id}`))}>
                  <span className="cr-time">
                    {time(e.starts_at)}
                    <span className="muted small">–{time(e.ends_at)}</span>
                  </span>
                  <span className="cr-what">
                    <strong>
                      {COURT_EVENT_LABEL[e.kind as CourtEventKind]}
                      {e.defendant ? ` — ${e.defendant}` : ''}
                    </strong>
                    <span className="small muted">
                      {e.court_name}
                      {e.courtroom ? `, ${e.courtroom}` : ''}
                      {e.court_case_number ? ` · ${e.court_case_number}` : ''}
                    </span>
                  </span>
                  <span className="cr-people small">
                    {e.people.map((p: any) => (
                      <span key={p.user_id + p.role}>
                        {COURT_ROLE_LABEL[p.role as CourtRole]}: {p.name}
                      </span>
                    ))}
                  </span>
                  <span className="cr-meta">
                    {e.status !== 'scheduled' && <Badge tone={STATUS_TONE[e.status]}>{COURT_STATUS_LABEL[e.status as CourtStatus]}</Badge>}
                    {e.evidence_open > 0 && <Badge tone="warn" dot>{e.evidence_open} evidence to decide</Badge>}
                  </span>
                </button>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

function CourtForm({ ev, onClose, onSaved }: { ev?: any; onClose: () => void; onSaved: (id: string) => void }) {
  const meta = useLoad(() => api.get<any>('/api/court/meta'), []);
  const [kind, setKind] = useState<CourtEventKind>(ev?.kind ?? 'trial');
  const [status, setStatus] = useState<CourtStatus>(ev?.status ?? 'scheduled');
  const [courtName, setCourtName] = useState(ev?.court_name ?? '');
  const [courtroom, setCourtroom] = useState(ev?.courtroom ?? '');
  const [date, setDate] = useState(ev ? iso(new Date(ev.starts_at)) : addDays(iso(new Date()), 7));
  const hhmm = (s: string) => new Date(s).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const [start, setStart] = useState(ev ? hhmm(ev.starts_at) : '09:00');
  const [end, setEnd] = useState(ev ? hhmm(ev.ends_at) : '12:00');
  const [courtCase, setCourtCase] = useState(ev?.court_case_number ?? '');
  const [defendant, setDefendant] = useState(ev?.defendant ?? '');
  const [cases, setCases] = useState<string>((ev?.case_numbers ?? []).join(', '));
  const [notes, setNotes] = useState(ev?.notes ?? '');
  const byRole = (r: CourtRole) => (ev?.people ?? []).filter((p: any) => p.role === r).map((p: any) => p.user_id);
  const [security, setSecurity] = useState<string[]>(byRole('security'));
  const [testifying, setTestifying] = useState<string[]>(byRole('testifying'));
  const [transport, setTransport] = useState<string[]>(byRole('transport'));
  const a = useAction();
  const people = meta.data?.people ?? [];
  const save = () =>
    a.run(async () => {
      const body = {
        kind, status, courtName, courtroom, date, start, end, courtCaseNumber: courtCase, defendant, notes,
        caseNumbers: cases.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean),
        people: [...security.map((userId) => ({ userId, role: 'security' })), ...testifying.map((userId) => ({ userId, role: 'testifying' })), ...transport.map((userId) => ({ userId, role: 'transport' }))],
      };
      const r = ev ? (await api.post<any>(`/api/court/${ev.id}`, body), { id: ev.id }) : await api.post<any>('/api/court', body);
      onSaved(r.id);
    });
  return (
    <div className="card stack court-form" role="dialog" aria-label={ev ? 'Change court date' : 'Schedule court'}>
      <h3>{ev ? 'Change this court date' : 'Schedule court'}</h3>
      <div className="grid-3">
        <label>
          What
          <select value={kind} onChange={(e) => setKind(e.target.value as CourtEventKind)}>
            {Object.entries(COURT_EVENT_LABEL).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label>
          Court
          <input list="court-names" value={courtName} onChange={(e) => setCourtName(e.target.value)} placeholder="Chester County Magistrate Court" />
          <datalist id="court-names">
            {(meta.data?.courts ?? []).map((c: string) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </label>
        <label>
          Courtroom
          <input value={courtroom} onChange={(e) => setCourtroom(e.target.value)} placeholder="Courtroom B" />
        </label>
        <label>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label>
          From
          <input type="time" value={start} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label>
          To
          <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
        </label>
        {kind !== 'security' && (
          <>
            <label>
              Defendant
              <input value={defendant} onChange={(e) => setDefendant(e.target.value)} />
            </label>
            <label>
              Court case # (warrant / indictment)
              <input value={courtCase} onChange={(e) => setCourtCase(e.target.value)} placeholder="2026A1210100456" />
            </label>
            <label>
              Sheriff’s report case #s
              <input value={cases} onChange={(e) => setCases(e.target.value)} placeholder="2026-000123, 2026-000140" />
              <span className="hint">Anything still in evidence for these cases is flagged so you can say what to do with it.</span>
            </label>
          </>
        )}
        {ev && (
          <label>
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value as CourtStatus)}>
              {Object.entries(COURT_STATUS_LABEL).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className="grid-3">
        <label>
          Courtroom security
          <PeoplePicker people={people} value={security} onChange={setSecurity} label="Courtroom security" placeholder="Who’s working security" />
        </label>
        {kind !== 'security' && (
          <label>
            Testifying
            <PeoplePicker people={people} value={testifying} onChange={setTestifying} label="Testifying" placeholder="Officer(s) testifying" />
          </label>
        )}
        <label>
          Prisoner transport
          <PeoplePicker people={people} value={transport} onChange={setTransport} label="Prisoner transport" placeholder="Optional" />
        </label>
      </div>
      <label>
        Notes
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Uniform, parking, what to bring…" />
      </label>
      <div className="row gap-sm">
        <button className="btn primary" disabled={!courtName.trim() || a.busy} onClick={save}>
          {ev ? 'Save and tell everyone' : 'Schedule and tell everyone'}
        </button>
        <button className="btn ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
      <ErrorBox error={a.error} />
    </div>
  );
}

export function CourtEvent({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/court/${id}`), [id]);
  const [editing, setEditing] = useState(false);
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  const e = r.data.event;
  const ev: any[] = r.data.evidence;
  const open = ev.filter((x) => x.disposition_status === 'open');
  return (
    <div className="ops stack court">
      <PageHeader
        crumb={<Link to={at('/court')}>Court calendar</Link>}
        title={`${COURT_EVENT_LABEL[e.kind as CourtEventKind]}${e.defendant ? ` — ${e.defendant}` : ''}`}
        sub={`${when(e.starts_at, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })} – ${time(e.ends_at)} · ${e.court_name}${e.courtroom ? `, ${e.courtroom}` : ''}`}
        actions={
          r.data.canSchedule && !editing && (
            <button className="btn" onClick={() => setEditing(true)}>
              Change
            </button>
          )
        }
      />
      {e.status !== 'scheduled' && <div className={`alert ${e.status === 'cancelled' ? 'warn' : 'info'}`}>This court date is {COURT_STATUS_LABEL[e.status as CourtStatus].toLowerCase()}.</div>}
      {editing && <CourtForm ev={e} onClose={() => setEditing(false)} onSaved={() => (setEditing(false), r.reload())} />}
      <div className="grid-2">
        <Card title="Who has to be there">
          {e.people.length === 0 ? (
            <p className="muted">Nobody scheduled.</p>
          ) : (
            <ul className="plain court-people">
              {e.people.map((p: any) => (
                <li key={p.user_id + p.role}>
                  <strong>{p.name}</strong> {p.call_number && <span className="dir-call">{p.call_number}</span>}
                  <div className="small muted">
                    {COURT_ROLE_LABEL[p.role as CourtRole]}
                    {p.phone && (
                      <>
                        {' · '}
                        <a href={`tel:${p.phone.replace(/[^\d+]/g, '')}`}>{p.phone}</a>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Case">
          <dl className="kv">
            {e.court_case_number && (
              <>
                <dt>Court case #</dt>
                <dd className="mono">{e.court_case_number}</dd>
              </>
            )}
            <dt>Report case #s</dt>
            <dd>{e.case_numbers.length ? e.case_numbers.join(', ') : <span className="muted">none</span>}</dd>
            <dt>Scheduled by</dt>
            <dd>{e.created_by_name}</dd>
            {e.notes && (
              <>
                <dt>Notes</dt>
                <dd className="pre">{e.notes}</dd>
              </>
            )}
          </dl>
          {r.data.unknownCases.length > 0 && <p className="small warn-text">No report found for {r.data.unknownCases.join(', ')} — check the number.</p>}
        </Card>
      </div>
      {e.case_numbers.length > 0 && (
        <Card title={open.length ? `In evidence — what do you want done with ${open.length === 1 ? 'it' : 'these'}?` : 'Evidence in this case'}>
          {ev.length === 0 ? (
            <p className="muted">Nothing in evidence for these cases.</p>
          ) : (
            <div className="disp-list">
              {ev.map((x) => (
                <DispositionRow key={x.id} x={x} eventId={e.id} canDecide={r.data.canSchedule} onSaved={r.reload} />
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function DispositionRow({ x, eventId, canDecide, onSaved }: { x: any; eventId: string; canDecide: boolean; onSaved: () => void }) {
  const { can } = useAuth();
  const canOpen = can('evidence.manage') || can('reports.read') || can('reports.approve');
  const [pick, setPick] = useState<DispositionDecision | null>(null);
  const [note, setNote] = useState('');
  const a = useAction();
  const closed = x.status === 'released' || x.status === 'destroyed';
  const decided = x.decision && x.disposition_status !== 'withdrawn';
  return (
    <div className={`disp ${x.disposition_status === 'open' ? 'open' : ''}`}>
      <div className="disp-h">
        {canOpen ? (
          <Link to={at(`/evidence/${x.id}`)} className="mono">
            {x.number}
          </Link>
        ) : (
          <span className="mono">{x.number}</span>
        )}
        <span>{x.description}</span>
        <span className="spacer" />
        <Badge tone={closed ? 'neutral' : 'ok'}>{EVIDENCE_STATUS_LABEL[x.status as keyof typeof EVIDENCE_STATUS_LABEL]}</Badge>
      </div>
      <div className="small muted">
        Case {x.case_number}
        {x.location ? ` · ${x.location}` : ''}
        {x.holder ? ` · with ${x.holder}` : ''}
      </div>
      {decided ? (
        <p className="small">
          <strong>Court: {DISPOSITION_LABEL[x.decision as DispositionDecision]}</strong>
          {x.order_note ? ` — ${x.order_note}` : ''} ({x.decided_by_name}, {when(x.decided_at)})
          {x.disposition_status === 'decided' && <Badge tone="warn">Evidence room to carry out</Badge>}
          {x.disposition_status === 'done' && x.decision !== 'hold' && <Badge tone="ok">Done</Badge>}
        </p>
      ) : null}
      {canDecide && !closed && (x.disposition_status === 'open' || pick) ? (
        <div className="disp-pick">
          {(Object.keys(DISPOSITION_LABEL) as DispositionDecision[]).map((d) => (
            <button key={d} type="button" className={`disp-opt ${pick === d ? 'on' : ''}`} onClick={() => setPick(d)} title={DISPOSITION_HINT[d]}>
              <strong>{DISPOSITION_LABEL[d]}</strong>
              <span className="small muted">{DISPOSITION_HINT[d]}</span>
            </button>
          ))}
          {pick && (
            <div className="row gap-sm disp-go">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Order details (optional) — e.g. after the 30-day appeal window" aria-label="Order details" />
              <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/court/${eventId}/evidence/${x.id}`, { decision: pick, note }), setPick(null), onSaved()))}>
                Order: {DISPOSITION_LABEL[pick]}
              </button>
              {x.disposition_status !== 'open' && (
                <button className="btn ghost" onClick={() => setPick(null)}>
                  Cancel
                </button>
              )}
            </div>
          )}
          <ErrorBox error={a.error} />
        </div>
      ) : canDecide && !closed && decided ? (
        <button className="btn sm disp-change" onClick={() => setPick(x.decision)}>
          Change the order
        </button>
      ) : null}
    </div>
  );
}
